/**
 * Employee service assignment, backed by the API.
 *
 * Assignments are read from the employee itself (`Employee.services`) and
 * written through their own sub-resource, which replaces the whole list.
 */

import type { Employee } from "@/types/employee";
import type { ServiceSpeciesAssignment } from "@/types/employeeServices";
import { adminUrl } from "@/utils/url";
import { HttpClient } from "../HttpClient";
import type { IEmployeesService } from "../employees/IEmployeesService";
import type { IEmployeeServicesService } from "./IEmployeeServicesService";
import { adaptEmployeeServices, toUpdateServicesBody } from "./adapters";

const employeeServicesPath = (employeeId: number) =>
  `/employees/${employeeId}/services`;

const hasServices = (value: unknown): value is Pick<Employee, "services"> =>
  typeof value === "object" &&
  value !== null &&
  Array.isArray((value as Employee).services);

type EmployeeServicesServiceDependencies = {
  employeesService: IEmployeesService;
};

export class EmployeeServicesService implements IEmployeeServicesService {
  private readonly employeesService: IEmployeesService;
  private readonly httpClient = new HttpClient({
    baseURL: adminUrl,
    timeout: 60000,
    headers: {},
  });

  constructor({ employeesService }: EmployeeServicesServiceDependencies) {
    this.employeesService = employeesService;
  }

  getAssignments(
    employeeId: number,
    signal?: AbortSignal,
  ): Promise<ServiceSpeciesAssignment[]> {
    return this.readAssignments(employeeId, signal);
  }

  async updateAssignments(
    employeeId: number,
    assignments: ServiceSpeciesAssignment[],
    signal?: AbortSignal,
  ): Promise<ServiceSpeciesAssignment[]> {
    const response = await this.httpClient.put<unknown>(
      employeeServicesPath(employeeId),
      toUpdateServicesBody(assignments),
      { signal },
    );

    /* An empty body tells nothing of what was stored, so read it back. */
    if (hasServices(response)) return adaptEmployeeServices(response.services);

    return this.readAssignments(employeeId, signal);
  }

  private async readAssignments(
    employeeId: number,
    signal?: AbortSignal,
  ): Promise<ServiceSpeciesAssignment[]> {
    const employee = await this.employeesService.getEmployeeById(
      String(employeeId),
      signal,
    );

    return adaptEmployeeServices(employee.services);
  }
}
